import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Maximize2, Minimize2, ZoomIn } from 'lucide-react';
import { getChordPlayer } from '@/audio/player';
import { useTransportState } from '@/audio/useSequencePlaying';
import { useStore } from '@/state/store';
import { workspaceScaleKind } from '@/state/displayScale';
import { ScaleSlider } from './ScaleSlider';

// Focus mode is a BUTTON (Oct 2026). It used to be a tap on the bare sheet,
// which made selecting bars fiddly (a tap meant to start a selection hid the
// chrome) and left no tap for the start cursor. Now a tap on a bar is the
// cursor, the view strip carries the button that hides the chrome, and while
// it is hidden the music has three floating controls:
//
// - a row at the foot of the screen, after Safari's bottom bar (user: "circles
//   seem to be the style, as well as ergonomics"): a capsule for the size of
//   the music, the reason to zoom in, and a circle for Play or Pause, under
//   the right thumb;
// - the way back, a circle at the top right, where every other close in the
//   app sits and under the Focus button it replaces (user ruling, Oct 2026,
//   moving it out of the bottom row: it "jumped" from the button's top right
//   to the bottom left).
//
// Everything else is one tap away on the full transport. They are separate
// pieces of frosted glass with the music showing between them, they fade
// together while the band plays, and they return on any movement, tap, key
// or scroll.

/** How long the floating controls stay after the reader last did anything. */
export const FOCUS_EXIT_IDLE_MS = 2500;

/** The view strip's button: hide the header and the transport. */
export function FocusToggle() {
  const setFocusMode = useStore((s) => s.setFocusMode);
  return (
    <button
      type="button"
      className="btn btn-sm popover-trigger workspace-focus-toggle"
      aria-label="Focus"
      title="Focus"
      data-testid="focus-toggle"
      onClick={() => setFocusMode(true)}
    >
      <Maximize2 size={17} strokeWidth={1.9} aria-hidden="true" />
    </button>
  );
}

/** The corner circle's width and its least margin from the screen's edge. */
const CORNER_SIZE = 50;
const CORNER_MARGIN = 12;

/**
 * Where the way back sits: under the Focus button it replaces, so it does not
 * jump across the screen (user, Oct 2026: from the top right of the page to
 * the bottom left). The button stays in the DOM while focused (its strip
 * slides up, never sideways), so its column can be read at any time. On a
 * wide screen the page is centred and that column is well in from the edge.
 * Null (the stylesheet's corner) when no button is on screen.
 */
function useCornerRight(): number | null {
  const [right, setRight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const place = () => {
      const toggle = [...document.querySelectorAll('[data-testid="focus-toggle"]')]
        .map((el) => el.getBoundingClientRect())
        .find((rect) => rect.width > 0);
      if (!toggle) return setRight(null);
      // clientWidth, not innerWidth: `right` is measured from the layout
      // viewport, which a classic scrollbar does not belong to.
      const width = document.documentElement.clientWidth;
      const centre = toggle.left + toggle.width / 2;
      const max = Math.max(CORNER_MARGIN, width - CORNER_SIZE - CORNER_MARGIN);
      setRight(
        Math.round(Math.min(max, Math.max(CORNER_MARGIN, width - centre - CORNER_SIZE / 2))),
      );
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, []);
  return right;
}

/** True for a while after the reader last moved, tapped, typed or scrolled. */
function useRecentActivity(ms: number): boolean {
  const [active, setActive] = useState(true);
  useEffect(() => {
    let timer = setTimeout(() => setActive(false), ms);
    const wake = () => {
      clearTimeout(timer);
      setActive(true);
      timer = setTimeout(() => setActive(false), ms);
    };
    const events = ['pointermove', 'pointerdown', 'keydown', 'wheel', 'scroll', 'touchstart'];
    for (const name of events) window.addEventListener(name, wake, { passive: true });
    return () => {
      clearTimeout(timer);
      for (const name of events) window.removeEventListener(name, wake);
    };
  }, [ms]);
  return active;
}

/** Focus a keyboard put there (`:focus-visible`), not a click's. */
function keyboardFocus(element: Element): boolean {
  try {
    return element.matches(':focus-visible');
  } catch {
    return true;
  }
}

/**
 * The dock's visibility: shown while `visible`, under the pointer or holding
 * keyboard focus. Faded, it keeps its hit area and each piece is covered by a
 * shield, so a tap only brings it back: let through, a tap on a faded Pause
 * would land on the bar underneath and move the band there, and a slider
 * moves on the press, before any click could be swallowed.
 */
function useFloat(visible: boolean) {
  const [held, setHeld] = useState(false);
  const focused = useRef(false);
  const shown = visible || held;
  return {
    shown,
    props: {
      'data-shown': shown || undefined,
      onPointerEnter: () => setHeld(true),
      onPointerLeave: () => setHeld(focused.current),
      onFocus: (event: { target: EventTarget }) => {
        // Keyboard focus only: a clicked button keeps focus too, and would
        // never fade again.
        if (!(event.target instanceof Element) || !keyboardFocus(event.target)) return;
        focused.current = true;
        setHeld(true);
      },
      onBlur: () => {
        focused.current = false;
        setHeld(false);
      },
    },
  };
}

/**
 * While the controls are hidden, a tap only brings them back (user, Oct 2026:
 * "tapping the score should bring back the tools but not move the playhead").
 * The click that ends a press begun while they were hidden is swallowed before
 * it reaches the sheet, so the band is not moved and no cursor is set; with
 * the controls showing, the next tap does what a tap does. A hold that selects
 * bars is a deliberate gesture and goes through: it works on the press, not
 * the click. The capture listeners on window run before anything on the page,
 * including the wake listeners, so the press sees the controls as they were.
 */
function useTapWakesOnly(shown: boolean) {
  const shownRef = useRef(shown);
  useLayoutEffect(() => {
    shownRef.current = shown;
  }, [shown]);
  useEffect(() => {
    let swallow = false;
    const press = () => {
      swallow = !shownRef.current;
    };
    const click = (event: MouseEvent) => {
      if (!swallow) return;
      swallow = false;
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener('pointerdown', press, true);
    window.addEventListener('click', click, true);
    return () => {
      window.removeEventListener('pointerdown', press, true);
      window.removeEventListener('click', click, true);
    };
  }, []);
}

/** One piece of the dock, with the shield that covers it while faded. */
function DockItem({
  className,
  shown,
  children,
}: {
  className: string;
  shown: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`focus-dock-item ${className}`}>
      {children}
      {!shown && <span className="focus-shield" aria-hidden="true" data-testid="focus-shield" />}
    </div>
  );
}

function FocusDock({ onPlay }: { onPlay: () => void }) {
  const setFocusMode = useStore((s) => s.setFocusMode);
  const audioEnabled = useStore((s) => s.audioEnabled);
  const transport = useTransportState();
  const playing = transport === 'playing';
  // One clock, started as focus begins. With sound the dock stays while the
  // band is paused or stopped, which is when you reach for it.
  const active = useRecentActivity(FOCUS_EXIT_IDLE_MS);
  const float = useFloat(active || (audioEnabled && !playing));
  useTapWakesOnly(float.shown);
  const kind = useStore((s) => workspaceScaleKind(s.viewSurface, s.voicingLens));
  const scale = useStore((s) => (kind === 'score' ? s.scoreScale : s.chartScale));
  const setScale = useStore((s) => (kind === 'score' ? s.setScoreScale : s.setChartScale));
  const label = kind === 'score' ? 'Score size' : 'Chord size';
  const cornerRight = useCornerRight();

  return (
    // One set of controls (one fade, one hover hold) in two places: the wrapper
    // draws no box of its own.
    <div className="focus-controls transport-surface" data-testid="focus-controls" {...float.props}>
      <div
        className="focus-corner"
        style={cornerRight == null ? undefined : { right: `${cornerRight}px` }}
      >
        <DockItem className="focus-dock-circle" shown={float.shown}>
          <button
            type="button"
            className="focus-dock-button"
            aria-label="Leave focus"
            title="Leave focus"
            data-testid="focus-exit"
            onClick={() => setFocusMode(false)}
          >
            <Minimize2 size={19} strokeWidth={2} aria-hidden="true" />
          </button>
        </DockItem>
      </div>
      <div className="focus-dock" data-testid="focus-dock">
        <DockItem className="focus-dock-capsule focus-scale" shown={float.shown}>
          <ZoomIn size={17} strokeWidth={1.9} aria-hidden="true" />
          <ScaleSlider
            value={scale}
            onChange={setScale}
            label={label}
            testid="focus-scale"
            presets={false}
          />
        </DockItem>
        {audioEnabled && (
          <DockItem className="focus-dock-circle focus-dock-play" shown={float.shown}>
            <button
              type="button"
              className="focus-dock-button"
              aria-label={playing ? 'Pause' : transport === 'paused' ? 'Resume' : 'Play'}
              data-testid="focus-play"
              onClick={() => {
                const player = getChordPlayer();
                if (playing) player.pause();
                else if (transport === 'paused') player.resumePlayback();
                else onPlay();
              }}
            >
              <span
                className={`transport-primary-icon transport-primary-icon-${playing ? 'pause' : 'play'}`}
                aria-hidden="true"
              />
            </button>
          </DockItem>
        )}
      </div>
    </div>
  );
}

/** While focused: the row at the foot of the screen and the way back top right. */
export function FocusControls({ onPlay }: { onPlay: () => void }) {
  const focusMode = useStore((s) => s.focusMode);
  return focusMode ? <FocusDock onPlay={onPlay} /> : null;
}
